import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/hooks/use-auth";
import { adminDashboardStats } from "@/lib/admin-dashboard.functions";
import { adminSitemapCheckSummary, adminRunSitemapCheck } from "@/lib/admin-sitemap-checks.functions";
import { adminRunSeoAudit, adminSeoAuditLatest } from "@/lib/admin-seo-audit.functions";
import { Package, FileText, Users, AlertCircle, TrendingUp, Sparkles, Mail, CreditCard, Webhook, Map, CheckCircle2, AlertTriangle, RefreshCw, Rocket, Clock, MessageSquare, Globe } from "lucide-react";
import { toast } from "sonner";



export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => ({ meta: [{ title: "Admin — Dashboard" }, { name: "robots", content: "noindex" }] }),
  component: AdminDashboard,
});

function AdminDashboard() {
  const { isAdmin, loading } = useAuth();
  const stats = useServerFn(adminDashboardStats);
  const sitemapSummary = useServerFn(adminSitemapCheckSummary);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-dashboard-stats"],
    queryFn: () => stats({}),
    enabled: isAdmin,
  });
  const { data: sitemap } = useQuery({
    queryKey: ["admin-dashboard-sitemap-latest"],
    queryFn: () => sitemapSummary(),
    enabled: isAdmin,
    refetchInterval: 60_000,
  });


  if (loading) return <div className="p-8">Laden…</div>;
  if (!isAdmin) return <div className="p-8">Geen toegang.</div>;

  const c = data?.counts;
  const eur = (n: number) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(n);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="text-muted-foreground">Overzicht van je webshop</p>
        </div>
        <PostPublishButton />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-28 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={TrendingUp} label="Omzet 30 dagen" value={eur(data?.revenue30 ?? 0)} accent="text-emerald-600" />
            <Stat icon={FileText} label="Bestellingen" value={c?.orders ?? 0} link="/admin/orders" />
            <Stat icon={FileText} label="Offertes" value={c?.quotes ?? 0} link="/admin/offertes" />
            <Stat icon={Users} label="Klanten" value={c?.customers ?? 0} link="/admin/klanten" />
            <Stat icon={Package} label="Producten" value={c?.products ?? 0} link="/admin/producten" />
            <Stat icon={AlertCircle} label="Zakelijk wachtrij" value={c?.pendingBusiness ?? 0} link="/admin/klanten" accent={c?.pendingBusiness ? "text-amber-600" : ""} />
            <Stat icon={Sparkles} label="Niet gesynchroniseerd" value={c?.channableUnsynced ?? 0} link="/admin/kanalen" />
            <Stat icon={AlertCircle} label="Channable fouten" value={c?.channableErrors ?? 0} link="/admin/kanalen" accent={c?.channableErrors ? "text-destructive" : ""} />
            <Stat icon={Mail} label="E-mail queue health" value="Bekijk" link="/admin/email-health" />
            <Stat icon={Mail} label="E-mail instellingen" value="Beheer" link="/admin/email-instellingen" />
            <Stat icon={CreditCard} label="Pin bij afhalen" value="Bekijk" link="/admin/openstaand-afhalen" />
            <Stat icon={Webhook} label="Mollie webhook events" value="Bekijk" link="/admin/webhook-events" />
            <Stat icon={Webhook} label="Stripe webhook events" value="Bekijk" link="/admin/stripe-webhooks" />
            <Stat icon={Map} label="Sitemap-checks" value="Bekijk" link="/admin/sitemap-checks" />
            <Stat icon={Globe} label="Stadspagina-bezoeken" value="Bekijk" link="/admin/city-hits" />
            <Stat icon={Package} label="Feed & productaudit" value="Bekijk" link="/admin/feed-audit" />

            <Stat icon={Sparkles} label="Social preview" value="Herlaad" link="/admin/social-preview" />
            <Stat icon={CheckCircle2} label="SEO-audit" value="Bekijk" link="/admin/seo-audit" />
            
            <Stat icon={Mail} label="WhatsApp Business" value="Beheer" link="/admin/whatsapp" />
            <Stat icon={MessageSquare} label="WhatsApp-sjablonen" value="Bewerk" link="/admin/whatsapp-templates" />
            <Stat icon={FileText} label="Algemene voorwaarden" value="Bewerk" link="/admin/algemene-voorwaarden" />

          </div>

          <LivePublishStatusCard />
          <SitemapStatusCard latest={(sitemap?.latest ?? null) as unknown as SitemapLatest | null} />



          <div className="rounded-xl border bg-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold">Recente bestellingen</h2>
              <Link to="/admin/orders" className="text-sm text-primary hover:underline">Alle bekijken →</Link>
            </div>
            {(data?.recentOrders.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">Nog geen bestellingen.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground uppercase">
                  <tr><th className="text-left pb-2">Nummer</th><th className="text-left pb-2">Datum</th><th className="text-left pb-2">Status</th><th className="text-right pb-2">Totaal</th></tr>
                </thead>
                <tbody>
                  {data?.recentOrders.map((o: any) => (
                    <tr key={o.id} className="border-t">
                      <td className="py-2 font-mono">{o.order_number}</td>
                      <td className="py-2">{new Date(o.created_at).toLocaleDateString("nl-NL")}</td>
                      <td className="py-2"><StatusBadge status={o.status} /></td>
                      <td className="py-2 text-right">{eur(o.total_cents / 100)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function PostPublishButton() {
  const qc = useQueryClient();
  const runAudit = useServerFn(adminRunSeoAudit);
  const runCheck = useServerFn(adminRunSitemapCheck);
  const mutation = useMutation({
    mutationFn: async () => {
      const [seo, sitemap] = await Promise.all([runAudit(), runCheck()]);
      return { seo, sitemap };
    },
    onSuccess: ({ seo, sitemap }) => {
      toast.success(
        `Post-publish check klaar — SEO: ${seo.totalUrls} URLs / ${seo.issueCount} bevindingen · Sitemap: ${sitemap.totalUrls} URLs${sitemap.ok ? "" : " (afwijkingen)"}`,
      );
      qc.invalidateQueries({ queryKey: ["admin-dashboard-sitemap-latest"] });
      qc.invalidateQueries({ queryKey: ["admin-sitemap-checks"] });
      qc.invalidateQueries({ queryKey: ["admin-seo-audit-runs"] });
      qc.invalidateQueries({ queryKey: ["admin-seo-audit-latest"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Post-publish check mislukt"),
  });
  return (
    <button
      type="button"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
      title="Voer direct SEO-audit + sitemap-check uit (bijv. na een publish)"
    >
      <RefreshCw className={`h-4 w-4 ${mutation.isPending ? "animate-spin" : ""}`} />
      {mutation.isPending ? "Bezig…" : "Post-publish audit"}
    </button>
  );
}

function Stat({ icon: Icon, label, value, link, accent }: { icon: any; label: string; value: number | string; link?: string; accent?: string }) {
  const body = (
    <div className="rounded-xl border bg-card p-5 hover:shadow-md transition">
      <div className="flex items-start justify-between mb-2">
        <Icon className="size-5 text-muted-foreground" />
      </div>
      <div className={`text-2xl font-bold ${accent ?? ""}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
  return link ? <Link to={link}>{body}</Link> : body;
}

interface SitemapLatest {
  ran_at: string;
  ok: boolean;
  total_urls: number;
  error: string | null;
  missing_vacancies: string[];
  bad_status: Array<{ url: string; status: number }>;
  redirects: Array<{ url: string; status: number; location?: string }>;
  robots_issues: string[];
  excluded_hits: string[];
}

function SitemapStatusCard({ latest }: { latest?: SitemapLatest | null }) {
  const qc = useQueryClient();
  const runCheck = useServerFn(adminRunSitemapCheck);
  const mutation = useMutation({
    mutationFn: () => runCheck(),
    onSuccess: (r) => {
      const missing = r.missingVacancies.length;
      if (r.ok) {
        toast.success(`Sitemap-check OK — ${r.totalUrls} URLs, geen afwijkingen.`);
      } else {
        toast.warning(
          `Afwijkingen: ${missing} ontbrekende vacature(s), ${r.badStatusCount} foutcode(s), ${r.redirectCount} redirect(s), ${r.robotsIssueCount} robots-issue(s).`,
        );
      }
      qc.invalidateQueries({ queryKey: ["admin-dashboard-sitemap-latest"] });
      qc.invalidateQueries({ queryKey: ["admin-sitemap-checks"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Check mislukt"),
  });

  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Map className="size-5 text-muted-foreground" />
          <h2 className="font-semibold">Laatste sitemap-check</h2>
          {latest && (
            latest.ok ? (
              <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                <CheckCircle2 className="h-3 w-3" /> OK
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                <AlertTriangle className="h-3 w-3" /> Faalt
              </span>
            )
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${mutation.isPending ? "animate-spin" : ""}`} />
            {mutation.isPending ? "Bezig…" : "Detecteer & regenereer"}
          </button>
          <Link to="/admin/sitemap-checks" className="text-sm text-primary hover:underline">Alle runs →</Link>
        </div>
      </div>


      {!latest ? (
        <p className="text-sm text-muted-foreground">Nog geen runs uitgevoerd.</p>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            Uitgevoerd {new Date(latest.ran_at).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "medium" })} · {latest.total_urls} URL{latest.total_urls === 1 ? "" : "s"} gecontroleerd
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
            <SummaryTile label="Missende vacatures" count={latest.missing_vacancies.filter((u) => u.includes("/werken-bij/")).length} tone="red" />
            <SummaryTile label="Foutcodes" count={latest.bad_status.length} tone="red" />
            <SummaryTile label="Redirects" count={latest.redirects.length} tone="amber" />
            <SummaryTile label="robots-issues" count={latest.robots_issues.length} tone="red" />
            <SummaryTile label="Uitgesloten in sitemap" count={latest.excluded_hits.length} tone="red" />
          </div>

          {latest.error && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              <strong>Fout:</strong> {latest.error}
            </div>
          )}

          {latest.robots_issues.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs">
              <div className="font-semibold text-red-800 mb-1">robots.txt-issues</div>
              <ul className="space-y-0.5 text-red-800">
                {latest.robots_issues.map((x) => <li key={x}>• {x}</li>)}
              </ul>
            </div>
          )}

          {latest.redirects.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs">
              <div className="font-semibold text-amber-800 mb-1">Redirect-afwijkingen ({latest.redirects.length})</div>
              <ul className="space-y-0.5 font-mono text-amber-900">
                {latest.redirects.slice(0, 5).map((r) => (
                  <li key={r.url}>• [{r.status}] {r.url}{r.location ? ` → ${r.location}` : ""}</li>
                ))}
                {latest.redirects.length > 5 && (
                  <li className="font-sans italic">+ {latest.redirects.length - 5} meer — zie volledig overzicht</li>
                )}
              </ul>
            </div>
          )}

          {latest.ok && !latest.error && latest.robots_issues.length === 0 && latest.redirects.length === 0 && (
            <p className="text-xs text-muted-foreground">Geen afwijkingen — sitemap en robots.txt zijn schoon.</p>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, count, tone }: { label: string; count: number; tone: "red" | "amber" }) {
  const active = count > 0;
  const activeColor = tone === "red" ? "text-red-600" : "text-amber-600";
  return (
    <div className="rounded-lg border bg-background p-3">
      <div className={`text-xl font-bold ${active ? activeColor : "text-muted-foreground"}`}>{count}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}



function LivePublishStatusCard() {
  const seoLatest = useServerFn(adminSeoAuditLatest);
  const sitemapSummary = useServerFn(adminSitemapCheckSummary);
  const seoQ = useQuery({
    queryKey: ["admin-seo-audit-latest"],
    queryFn: () => seoLatest(),
    refetchInterval: (q) => (q.state.error ? 120_000 : 60_000),
    retry: 2,
    retryDelay: (i) => Math.min(1000 * 2 ** i, 15_000),
  });
  const smQ = useQuery({
    queryKey: ["admin-dashboard-sitemap-latest"],
    queryFn: () => sitemapSummary(),
    refetchInterval: (q) => (q.state.error ? 120_000 : 60_000),
    retry: 2,
    retryDelay: (i) => Math.min(1000 * 2 ** i, 15_000),
  });

  const seoRun = (seoQ.data?.latest ?? null) as {
    ran_at?: string;
    total_urls?: number;
    issue_count?: number;
    ok?: boolean;
  } | null;
  const smRun = (smQ.data?.latest ?? null) as {
    ran_at?: string;
    total_urls?: number;
    ok?: boolean;
  } | null;

  const seoErr = seoQ.error as Error | null;
  const smErr = smQ.error as Error | null;
  const anyError = Boolean(seoErr || smErr);
  const initialLoading = (seoQ.isLoading && !seoQ.data) || (smQ.isLoading && !smQ.data);

  const seoAge = seoRun?.ran_at ? Date.now() - new Date(seoRun.ran_at).getTime() : null;
  const smAge = smRun?.ran_at ? Date.now() - new Date(smRun.ran_at).getTime() : null;
  const staleMs = 6 * 60 * 60 * 1000; // 6 uur
  const pending: string[] = [];
  if (seoErr) pending.push("SEO-audit status onbereikbaar");
  if (smErr) pending.push("Sitemap-check status onbereikbaar");
  if (!seoErr && (!seoRun || (seoAge !== null && seoAge > staleMs))) pending.push("SEO-audit verlopen");
  if (!smErr && (!smRun || (smAge !== null && smAge > staleMs))) pending.push("Sitemap-check verlopen");
  if (seoRun && (seoRun.issue_count ?? 0) > 0) pending.push(`${seoRun.issue_count} SEO-bevinding(en)`);
  if (smRun && smRun.ok === false) pending.push("Sitemap-afwijkingen");

  const allGood = !anyError && pending.length === 0 && seoRun && smRun;

  const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }) : "—");
  const rel = (ms: number | null) => {
    if (ms === null) return "onbekend";
    const min = Math.round(ms / 60000);
    if (min < 1) return "zojuist";
    if (min < 60) return `${min} min geleden`;
    const h = Math.round(min / 60);
    if (h < 24) return `${h} uur geleden`;
    const d = Math.round(h / 24);
    return `${d} dag${d === 1 ? "" : "en"} geleden`;
  };

  const retryAll = () => {
    void seoQ.refetch();
    void smQ.refetch();
  };

  const badge = (() => {
    if (initialLoading) {
      return (
        <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
          <RefreshCw className="h-3 w-3 animate-spin" /> Status laden…
        </span>
      );
    }
    if (anyError) {
      return (
        <span className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
          <AlertCircle className="h-3 w-3" /> Status niet beschikbaar
        </span>
      );
    }
    if (allGood) {
      return (
        <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
          <CheckCircle2 className="h-3 w-3" /> Live & schoon
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
        <AlertTriangle className="h-3 w-3" /> {pending.length} openstaand
      </span>
    );
  })();

  const Tile = ({
    icon,
    label,
    error,
    isFetching,
    hasData,
    primary,
    sub,
  }: {
    icon: React.ReactNode;
    label: string;
    error: Error | null;
    isFetching: boolean;
    hasData: boolean;
    primary: string;
    sub: string;
  }) => (
    <div className={`rounded-lg border p-3 ${error ? "border-red-200 bg-red-50" : "bg-background"}`}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon} {label}
        {isFetching && <RefreshCw className="h-3 w-3 animate-spin ml-auto" />}
      </div>
      {error ? (
        <div className="mt-1 text-sm text-red-800">
          <div className="font-semibold">Kon status niet ophalen</div>
          <div className="text-[11px] text-red-700 truncate" title={error.message}>{error.message || "Onbekende fout"}</div>
        </div>
      ) : hasData ? (
        <>
          <div className="mt-1 font-semibold">{primary}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
            <Clock className="h-3 w-3" /> {sub}
          </div>
        </>
      ) : (
        <div className="mt-1 text-sm text-muted-foreground">Nog niet gedraaid</div>
      )}
    </div>
  );

  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Rocket className="size-5 text-muted-foreground" />
          <h2 className="font-semibold">Publish-status</h2>
          {badge}
        </div>
        {(anyError || !initialLoading) && (
          <button
            type="button"
            onClick={retryAll}
            disabled={seoQ.isFetching || smQ.isFetching}
            className="inline-flex items-center gap-1 rounded border bg-background px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${seoQ.isFetching || smQ.isFetching ? "animate-spin" : ""}`} />
            Opnieuw proberen
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <Tile
          icon={<CheckCircle2 className="h-3.5 w-3.5" />}
          label="Laatste SEO-audit"
          error={seoErr}
          isFetching={seoQ.isFetching}
          hasData={Boolean(seoRun)}
          primary={seoRun ? `${seoRun.total_urls ?? 0} URLs · ${seoRun.issue_count ?? 0} issues` : ""}
          sub={`${rel(seoAge)} · ${fmt(seoRun?.ran_at)}`}
        />
        <Tile
          icon={<Map className="h-3.5 w-3.5" />}
          label="Laatste sitemap-check"
          error={smErr}
          isFetching={smQ.isFetching}
          hasData={Boolean(smRun)}
          primary={smRun ? `${smRun.total_urls ?? 0} URLs · ${smRun.ok ? "OK" : "afwijkingen"}` : ""}
          sub={`${rel(smAge)} · ${fmt(smRun?.ran_at)}`}
        />
      </div>

      {anyError && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
          <div className="font-semibold mb-1">Live status polling faalde</div>
          <p>
            De laatste status kon niet worden opgehaald. We tonen de laatst bekende gegevens (indien beschikbaar) en
            proberen automatisch opnieuw elke 2 minuten. Gebruik "Opnieuw proberen" om direct te herladen.
          </p>
        </div>
      )}

      {!anyError && pending.length > 0 && (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <div className="font-semibold mb-1">Openstaand na laatste publish</div>
          <ul className="space-y-0.5">
            {pending.map((p) => <li key={p}>• {p}</li>)}
          </ul>
          <p className="mt-1 text-[11px] text-amber-800">
            Klik rechtsboven op "Post-publish audit" om SEO + sitemap opnieuw te draaien.
          </p>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: "bg-amber-100 text-amber-800",
    processing: "bg-blue-100 text-blue-800",
    completed: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-red-100 text-red-800",
  };
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors[status] ?? "bg-muted"}`}>{status}</span>;
}
